import type { FileUIPart } from "ai";

import type { AccessTokenGetter } from "@/lib/utils";
import { fetchWithAuth } from "@/lib/utils";
import type { Project } from "@/lib/project-types";

export type QuickLaunchTargetKind = "agent" | "project";

export interface QuickLaunchAttachmentMetadata {
  filename: string;
  media_type: string;
  size_bytes: number;
}

export interface QuickLaunchRouteRequest {
  quick_launch_id: string;
  task_text: string;
  transcript: string;
  attachments: QuickLaunchAttachmentMetadata[];
}

export interface QuickLaunchTarget {
  kind: QuickLaunchTargetKind;
  id: string;
  name: string;
  description: string;
  default_agent_id?: string;
  default_agent_name?: string;
}

export interface QuickLaunchRouteResponse {
  target: QuickLaunchTarget | null;
  reason_code: "matched" | "no_suitable_target";
}

export interface QuickLaunchTranscriptionResponse {
  text: string;
  language_code: string | null;
  duration_seconds: number | null;
  filename: string;
  mime_type: string;
}

export class QuickLaunchApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "QuickLaunchApiError";
    this.status = status;
  }
}

type PromptFile = FileUIPart & { rawFile?: File };

function safeFilename(value: unknown): string {
  const raw = typeof value === "string" ? value : "attachment";
  // File names arrive from the browser, but do not let a hand-built FileUIPart
  // turn metadata into an uploaded path or URL.
  if (/^(?:blob:|data:|file:|https?:)/i.test(raw.trim())) return "attachment";
  const basename = raw.split(/[\\/]/).filter(Boolean).at(-1) ?? "attachment";
  return basename.replaceAll("\0", "").slice(0, 255) || "attachment";
}

function safeMediaType(value: unknown): string {
  const mediaType = typeof value === "string" ? value.trim() : "";
  return /^[A-Za-z0-9!#$&^_.+-]+\/[A-Za-z0-9!#$&^_.+-]+$/.test(mediaType)
    ? mediaType.slice(0, 128)
    : "application/octet-stream";
}

/** Convert prompt file parts to the metadata-only routing contract. */
export function toQuickLaunchAttachmentMetadata(
  files: readonly PromptFile[],
): QuickLaunchAttachmentMetadata[] {
  return files.slice(0, 10).map((file) => ({
    filename: safeFilename(file.filename),
    media_type: safeMediaType(file.mediaType),
    size_bytes: Math.max(0, Math.min(file.rawFile?.size ?? 0, 2 * 1024 * 1024 * 1024)),
  }));
}

async function parseError(response: Response, fallback: string): Promise<never> {
  const body = await response.text().catch(() => "");
  throw new QuickLaunchApiError(
    response.status,
    body || `${fallback}: ${response.status} ${response.statusText}`,
  );
}

/** Ask the same-origin bridge to recommend an accessible target. */
export async function routeQuickLaunch(
  request: QuickLaunchRouteRequest,
  getToken: AccessTokenGetter,
  signal?: AbortSignal,
): Promise<QuickLaunchRouteResponse> {
  const response = await fetchWithAuth(
    "/api/quick-launch/route",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        quick_launch_id: request.quick_launch_id,
        task_text: request.task_text,
        transcript: request.transcript,
        attachments: request.attachments.map((attachment) => ({
          filename: safeFilename(attachment.filename),
          media_type: safeMediaType(attachment.media_type),
          size_bytes: Math.max(0, Math.min(attachment.size_bytes, 2 * 1024 * 1024 * 1024)),
        })),
      }),
      signal,
    },
    getToken,
  );
  if (!response.ok) return parseError(response, "Quick launch routing failed");
  return response.json() as Promise<QuickLaunchRouteResponse>;
}

/** Record one successfully launched target; callers should treat this as best effort. */
export async function recordQuickLaunchUsage(
  target: QuickLaunchTarget,
  getToken: AccessTokenGetter,
): Promise<void> {
  const response = await fetchWithAuth(
    "/api/quick-launch/usage",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        target: { kind: target.kind, id: target.id },
      }),
    },
    getToken,
  );
  if (!response.ok) await parseError(response, "Quick launch usage recording failed");
}

/** Load a full accessible project only after a user selects its catalog row. */
export async function getProject(
  projectId: string,
  getToken: AccessTokenGetter,
  signal?: AbortSignal,
): Promise<Project> {
  const response = await fetchWithAuth(
    `/api/projects/${encodeURIComponent(projectId)}`,
    { headers: {}, signal },
    getToken,
  );
  if (!response.ok) return parseError(response, "Could not load project");
  return response.json() as Promise<Project>;
}

/** Transcribe a quick-launch recording before a session exists. */
export async function transcribeQuickLaunchAudio(
  audio: File,
  languageCode: string | undefined,
  costRunId: string,
  getToken: AccessTokenGetter,
  signal?: AbortSignal,
): Promise<QuickLaunchTranscriptionResponse> {
  const params = new URLSearchParams();
  if (languageCode) params.set("language_code", languageCode);
  params.set("cost_run_id", costRunId);
  const formData = new FormData();
  formData.append("files", audio, audio.name);
  const response = await fetchWithAuth(
    `/api/transcribe?${params.toString()}`,
    { method: "POST", body: formData, signal },
    getToken,
  );
  if (!response.ok) return parseError(response, "Voice transcription failed");
  return response.json() as Promise<QuickLaunchTranscriptionResponse>;
}
